import { useCallback, useState } from 'react';
import { useWebSocket } from './useWebSocket';
import { playerConfig } from '../config';

export interface UseAdBreakReturn {
  /** Whether an ad break is currently active */
  adBreakActive: boolean;
  /** Current ad index (1-based) within the break */
  currentAd: number;
  /** Total number of ads in the current break */
  totalAds: number;
  /** Remaining seconds in the current ad */
  remaining: number;
  /** Whether the WebSocket is connected */
  connected: boolean;
}

/**
 * Hook for real-time ad break metadata via WebSocket (Req 1.25, 1.26).
 *
 * Receives:
 * - `ad_break_start`: signals the beginning of an ad break
 * - `ad_progress`: updates current ad index, total, and remaining time
 * - `ad_break_end`: signals the end of the ad break
 */
export function useAdBreak(): UseAdBreakReturn {
  const [adBreakActive, setAdBreakActive] = useState(false);
  const [currentAd, setCurrentAd] = useState(0);
  const [totalAds, setTotalAds] = useState(0);
  const [remaining, setRemaining] = useState(0);

  const url = buildWebSocketUrl(playerConfig.wsAdBreakUrl);

  const handleMessage = useCallback((data: unknown) => {
    if (!isAdBreakMessage(data)) return;

    switch (data.type) {
      case 'ad_break_start':
        setAdBreakActive(true);
        setCurrentAd(data.currentAd ?? 1);
        setTotalAds(data.totalAds ?? 0);
        setRemaining(data.remaining ?? 0);
        break;

      case 'ad_progress':
        setCurrentAd(data.currentAd ?? currentAd);
        setTotalAds(data.totalAds ?? totalAds);
        setRemaining(data.remaining ?? 0);
        break;

      case 'ad_break_end':
        setAdBreakActive(false);
        setCurrentAd(0);
        setTotalAds(0);
        setRemaining(0);
        break;
    }
  }, [currentAd, totalAds]);

  useWebSocket({
    url,
    onMessage: handleMessage,
    autoConnect: !!playerConfig.wsAdBreakUrl,
  });

  return { adBreakActive, currentAd, totalAds, remaining, connected: adBreakActive || url !== '' };
}

function buildWebSocketUrl(baseUrl: string): string {
  if (!baseUrl) return '';
  const channelId = playerConfig.channelId;
  const token = playerConfig.wsToken;
  return `${baseUrl}?token=${encodeURIComponent(token)}&channelId=${encodeURIComponent(channelId)}`;
}

interface AdBreakMessage {
  type: 'ad_break_start' | 'ad_progress' | 'ad_break_end';
  currentAd?: number;
  totalAds?: number;
  remaining?: number;
}

function isAdBreakMessage(data: unknown): data is AdBreakMessage {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return (
    msg.type === 'ad_break_start' ||
    msg.type === 'ad_progress' ||
    msg.type === 'ad_break_end'
  );
}
